/**
 * Release 1 F2 — saved views switcher for the board header: pick a view ("Board views" shared via MANAGE_BOARD,
 * "My views" private), save the current filter/sort/display tuple as a new view, update / rename / set default /
 * delete the active one. A dot after the name marks unsaved changes. Also hosts the Display menu (density, group by).
 */
import { useState } from 'react';
import { Bookmark, Check, ChevronDown, LayoutGrid, Star } from 'lucide-react';
import { useBoardViews, useCreateBoardView, useDeleteBoardView, useUpdateBoardView, type BoardView } from '@/lib/api/queries';
import { useSessionStore } from '@/stores/session';
import { toast } from '@/stores/ui';
import { isApiError } from '@/lib/api/errors';
import { Button } from '@/components/Button';
import { Dialog } from '@/components/Dialog';
import { Input, Select } from '@/components/Input';
import { Menu, type MenuItem } from '@/components/Menu';
import { usePermission } from '@/features/workspace/usePermission';
import type { ViewDensity, ViewGroupBy } from '@/stores/gridPrefs';
import { cx } from '@/lib/format';
import { tupleBody, type ViewTuple } from './viewState';

export function ViewSwitcher({ boardId, active, dirty, tuple, onSelect }: { boardId: string; active: BoardView | null; dirty: boolean; tuple: ViewTuple; onSelect: (view: BoardView | null) => void }) {
  const views = useBoardViews(boardId);
  const me = useSessionStore((s) => s.membership?.member_id ?? null);
  const canManage = usePermission('MANAGE_BOARD', { boardId });
  const create = useCreateBoardView(boardId);
  const update = useUpdateBoardView(boardId);
  const del = useDeleteBoardView(boardId);
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const open = anchor != null;
  const [saving, setSaving] = useState<{ name: string; visibility: 'private' | 'board'; is_default: boolean } | null>(null);
  const [renaming, setRenaming] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

  const list = views.data ?? [];
  const shared = list.filter((v) => v.visibility === 'board');
  const mine = list.filter((v) => v.visibility === 'private');
  const canEditActive = !!active && (active.visibility === 'board' ? canManage : active.owner_member_id === me);
  const fail = (e: unknown, what: string) => toast({ kind: 'error', title: what, body: isApiError(e) && e.status === 409 ? 'A view with that name already exists.' : isApiError(e) ? e.message : undefined });

  const item = (v: BoardView): MenuItem => ({
    label: (
      <span className="flex items-center gap-1.5">
        {v.id === active?.id ? <Check size={12} /> : <span className="w-3" />}
        {v.name}
        {v.is_default && <Star size={11} className="text-fg-subtle" aria-label="default" />}
      </span>
    ),
    onSelect: () => onSelect(v),
  });
  const items: MenuItem[] = [
    { label: <span className="flex items-center gap-1.5">{active ? <span className="w-3" /> : <Check size={12} />}All assets</span>, onSelect: () => onSelect(null) },
    ...(shared.length ? [{ kind: 'label' as const, label: 'Board views' }, ...shared.map(item)] : []),
    ...(mine.length ? [{ kind: 'label' as const, label: 'My views' }, ...mine.map(item)] : []),
    { kind: 'separator' },
    { label: 'Save as new view…', icon: <Bookmark size={14} />, onSelect: () => setSaving({ name: active ? `${active.name} copy` : '', visibility: 'private', is_default: false }) },
    ...(active
      ? [
          { label: `Update “${active.name}”`, disabled: !dirty || !canEditActive, title: !canEditActive ? 'You cannot change this view' : undefined, onSelect: () => update.mutate({ viewId: active.id, body: tupleBody(tuple) }, { onSuccess: (v) => { onSelect(v); toast({ kind: 'success', title: 'View updated' }); }, onError: (e) => fail(e, 'Could not update view') }) },
          { label: 'Rename…', disabled: !canEditActive, onSelect: () => setRenaming(active.name) },
          ...(active.visibility === 'board' && canManage ? [{ label: active.is_default ? 'Remove as default' : 'Set as board default', onSelect: () => update.mutate({ viewId: active.id, body: { is_default: !active.is_default } }, { onError: (e) => fail(e, 'Could not change default') }) }] : []),
          { label: 'Delete…', danger: true, disabled: !canEditActive, onSelect: () => setDeleting(true) },
        ]
      : []),
  ];

  return (
    <>
      <button type="button" aria-haspopup="menu" aria-expanded={open} aria-label="Views" onClick={(e) => setAnchor(e.currentTarget)} data-view-switcher className={cx('inline-flex h-7 items-center gap-1 rounded-md border border-line px-2 text-xs font-medium hover:bg-bg-sunken', active && 'text-fg')}>
        <Bookmark size={13} className="text-fg-subtle" />
        <span data-view-name>{active ? active.name : 'All assets'}</span>
        {dirty && <span className="h-1.5 w-1.5 rounded-full bg-accent" aria-label="unsaved changes" title="Unsaved changes" data-view-dirty />}
        <ChevronDown size={12} className="opacity-70" />
      </button>
      {open && <Menu anchor={anchor} items={items} onClose={() => setAnchor(null)} align="end" />}
      {saving && (
        <Dialog
          open
          onClose={() => setSaving(null)}
          title="Save view"
          footer={
            <>
              <Button onClick={() => setSaving(null)}>Cancel</Button>
              <Button variant="primary" disabled={!saving.name.trim() || create.isPending} onClick={() => submitSave()}>Save</Button>
            </>
          }
        >
          <form
            className="space-y-3 text-sm"
            onSubmit={(e) => {
              e.preventDefault();
              submitSave();
            }}
          >
            <label className="block">
              <span className="mb-1 block text-xs text-fg-muted">Name</span>
              <Input autoFocus value={saving.name} onChange={(e) => setSaving({ ...saving, name: e.target.value })} aria-label="View name" />
            </label>
            <label className="block">
              <span className="mb-1 block text-xs text-fg-muted">Visibility</span>
              <Select aria-label="Visibility" value={saving.visibility} onChange={(e) => setSaving({ ...saving, visibility: e.target.value as 'private' | 'board', is_default: e.target.value === 'board' ? saving.is_default : false })}>
                <option value="private">Only me</option>
                <option value="board" disabled={!canManage}>Everyone on this board{canManage ? '' : ' (needs Manage board)'}</option>
              </Select>
            </label>
            {saving.visibility === 'board' && (
              <label className="flex items-center gap-2 text-xs">
                <input type="checkbox" checked={saving.is_default} onChange={(e) => setSaving({ ...saving, is_default: e.target.checked })} /> Open this board with this view by default
              </label>
            )}
            <p className="text-xs text-fg-subtle">Saves the current filters, sort, layout, density, titles and grouping.</p>
          </form>
        </Dialog>
      )}
      {renaming != null && active && (
        <Dialog open onClose={() => setRenaming(null)} title="Rename view" footer={<><Button onClick={() => setRenaming(null)}>Cancel</Button><Button variant="primary" disabled={!renaming.trim() || update.isPending} onClick={() => submitRename()}>Rename</Button></>}>
          <Input autoFocus value={renaming} onChange={(e) => setRenaming(e.target.value)} aria-label="View name" onKeyDown={(e) => e.key === 'Enter' && submitRename()} />
        </Dialog>
      )}
      {deleting && active && (
        <Dialog open onClose={() => setDeleting(false)} title="Delete view" footer={<><Button onClick={() => setDeleting(false)}>Cancel</Button><Button variant="danger" disabled={del.isPending} onClick={() => del.mutate(active.id, { onSuccess: () => { setDeleting(false); onSelect(null); toast({ kind: 'success', title: 'View deleted' }); }, onError: (e) => fail(e, 'Could not delete view') })}>Delete</Button></>}>
          <p className="text-sm">Delete “{active.name}”? {active.visibility === 'board' ? 'Everyone on this board loses it.' : 'Only you can see it.'}</p>
        </Dialog>
      )}
    </>
  );

  function submitSave() {
    if (!saving || !saving.name.trim() || create.isPending) return;
    create.mutate(
      { name: saving.name.trim(), visibility: saving.visibility, is_default: saving.is_default, ...tupleBody(tuple) },
      {
        onSuccess: (v) => {
          setSaving(null);
          onSelect(v);
          toast({ kind: 'success', title: 'View saved', body: v.visibility === 'board' ? 'Visible to everyone on this board.' : 'Visible only to you.' });
        },
        onError: (e) => fail(e, 'Could not save view'),
      },
    );
  }
  function submitRename() {
    if (!active || !renaming?.trim() || update.isPending) return;
    update.mutate({ viewId: active.id, body: { name: renaming.trim() } }, { onSuccess: () => setRenaming(null), onError: (e) => fail(e, 'Could not rename view') });
  }
}

const DENSITIES: Array<{ id: ViewDensity; label: string }> = [
  { id: 'compact', label: 'Compact' },
  { id: 'comfortable', label: 'Comfortable' },
  { id: 'spacious', label: 'Spacious' },
];
const GROUPS: Array<{ id: ViewGroupBy | null; label: string }> = [
  { id: null, label: 'No grouping' },
  { id: 'type', label: 'Type' },
  { id: 'approval', label: 'Approval' },
  { id: 'uploaded_by', label: 'Uploaded by' },
  { id: 'date_added', label: 'Date added' },
];

/** Density and group-by (persisted per view, or per board when no view is active). */
export function DisplayMenu({ density, groupBy, onDensity, onGroupBy }: { density: ViewDensity; groupBy: ViewGroupBy | null; onDensity: (d: ViewDensity) => void; onGroupBy: (g: ViewGroupBy | null) => void }) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const open = anchor != null;
  const check = (on: boolean) => (on ? <Check size={12} /> : <span className="inline-block w-3" />);
  const items: MenuItem[] = [
    { kind: 'label', label: 'Density' },
    ...DENSITIES.map((d): MenuItem => ({ label: <span className="flex items-center gap-1.5">{check(density === d.id)}{d.label}</span>, onSelect: () => onDensity(d.id) })),
    { kind: 'separator' },
    { kind: 'label', label: 'Group by' },
    ...GROUPS.map((g): MenuItem => ({ label: <span className="flex items-center gap-1.5">{check(groupBy === g.id)}{g.label}</span>, onSelect: () => onGroupBy(g.id) })),
  ];
  return (
    <>
      <button type="button" aria-haspopup="menu" aria-expanded={open} aria-label="Display options" onClick={(e) => setAnchor(e.currentTarget)} data-display-menu className="inline-flex h-7 items-center gap-1 rounded-md border border-line px-2 text-xs font-medium hover:bg-bg-sunken">
        <LayoutGrid size={13} className="text-fg-subtle" />
        {groupBy ? `Grouped · ${GROUPS.find((g) => g.id === groupBy)?.label}` : 'Display'}
        <ChevronDown size={12} className="opacity-70" />
      </button>
      {open && <Menu anchor={anchor} items={items} onClose={() => setAnchor(null)} align="end" />}
    </>
  );
}
